import React, { useEffect, useState } from 'react';
import { SendIcon, BotIcon, UserIcon, TrendingUpIcon, SunIcon, CloudRainIcon, ThermometerIcon, ActivityIcon, CalendarIcon, ZapIcon, ShieldIcon, MoonIcon, DropletIcon } from 'lucide-react';
import GlassCard from '../ui/GlassCard';
import Button from '../ui/Button';
import { useHydration } from '../../contexts/HydrationContext';
const AICoaching: React.FC = () => {
  const {
    dailyGoal,
    currentIntake,
    userProfile
  } = useHydration();
  const [message, setMessage] = useState('');
  const [weather, setWeather] = useState({
    temp: 82,
    condition: 'sunny',
    humidity: 65
  });
  const [activity, setActivity] = useState({
    steps: 4500,
    workouts: 1
  });
  const [functionalShotsVisible, setFunctionalShotsVisible] = useState(false);
  const hydrationScore = 78;
  // Simulate weather and activity data fetch
  useEffect(() => {
    // This would normally come from an API or device integration
    const interval = setInterval(() => {
      setActivity(prev => ({
        ...prev,
        steps: prev.steps + Math.floor(Math.random() * 100)
      }));
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const messages = [{
    id: 1,
    sender: 'bot',
    text: `Hi ${userProfile.name}! I've analyzed your hydration patterns this week. You're doing great on weekdays but tend to forget on weekends.`,
    time: '9:45 AM'
  }, {
    id: 2,
    sender: 'user',
    text: 'How can I improve my overall score?',
    time: '9:47 AM'
  }, {
    id: 3,
    sender: 'bot',
    text: `With today's temperature at ${weather.temp}°F and your activity level, I recommend increasing your water intake by 20%. Also, consider adding our Energy functional shot since you have a workout scheduled later.`,
    time: '9:48 AM'
  }];
  const functionalShots = [{
    id: 'energy',
    name: 'Energy Boost',
    icon: ZapIcon,
    description: 'Natural caffeine & B vitamins',
    color: '#FF9800'
  }, {
    id: 'immunity',
    name: 'Immunity',
    icon: ShieldIcon,
    description: 'Vitamin C & zinc complex',
    color: '#A6FF4D'
  }, {
    id: 'relaxation',
    name: 'Relaxation',
    icon: MoonIcon,
    description: 'L-theanine & chamomile',
    color: '#9C27B0'
  }, {
    id: 'alkaline',
    name: 'Alkaline+',
    icon: DropletIcon,
    description: 'pH balanced with electrolytes',
    color: '#00E4BB'
  }];
  const handleSendMessage = () => {
    if (message.trim()) {
      // In a real app, we would add the message to the messages array
      // and send it to the backend
      setMessage('');
    }
  };
  const toggleFunctionalShots = () => {
    setFunctionalShotsVisible(!functionalShotsVisible);
  };
  return <div className="h-full bg-[#1A237E] pt-12 pb-24 px-4 flex flex-col" data-id="element-20">
      <header className="mb-6" data-id="element-21">
        <h1 className="text-2xl font-bold text-white font-gt-ultra" data-id="element-22">
          AI Hydration Coach
        </h1>
        <p className="text-[#00E4BB] font-gill-sans" data-id="element-23">
          Personalized guidance & insights
        </p>
      </header>

      {/* Smart Context Card */}
      <GlassCard className="mb-6 p-4" data-id="element-24">
        <div className="flex justify-between items-center mb-3" data-id="element-25">
          <h3 className="text-white font-bold" data-id="element-26">Smart Context</h3>
          <span className="text-xs text-[#00E4BB]" data-id="element-27">Updated just now</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-28">
          <div className="flex flex-col items-center bg-[#1A237E]/50 rounded-lg p-2" data-id="element-29">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#FF9800]/20 mb-1" data-id="element-30">
              <ThermometerIcon className="w-4 h-4 text-[#FF9800]" data-id="element-31" />
            </div>
            <span className="text-xs text-gray-300" data-id="element-32">Temperature</span>
            <span className="text-[#FF9800] font-medium" data-id="element-33">{weather.temp}°F</span>
          </div>
          <div className="flex flex-col items-center bg-[#1A237E]/50 rounded-lg p-2" data-id="element-34">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#A6FF4D]/20 mb-1" data-id="element-35">
              <ActivityIcon className="w-4 h-4 text-[#A6FF4D]" data-id="element-36" />
            </div>
            <span className="text-xs text-gray-300" data-id="element-37">Activity</span>
            <span className="text-[#A6FF4D] font-medium" data-id="element-38">
              {activity.steps} steps
            </span>
          </div>
          <div className="flex flex-col items-center bg-[#1A237E]/50 rounded-lg p-2" data-id="element-39">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#00E4BB]/20 mb-1" data-id="element-40">
              <CalendarIcon className="w-4 h-4 text-[#00E4BB]" data-id="element-41" />
            </div>
            <span className="text-xs text-gray-300" data-id="element-42">Calendar</span>
            <span className="text-[#00E4BB] font-medium" data-id="element-43">Workout @ 5PM</span>
          </div>
        </div>
        <div className="bg-[#1A237E]/50 rounded-lg p-3" data-id="element-44">
          <p className="text-white text-sm" data-id="element-45">
            <span className="text-[#FF9800] font-medium" data-id="element-46">
              High temperature alert:
            </span>{' '}
            It's hotter than usual today. Consider increasing your water intake
            by 20% to stay properly hydrated.
          </p>
        </div>
      </GlassCard>

      {/* Hydration Score Card */}
      <GlassCard className="mb-6 p-4" data-id="element-47">
        <div className="flex items-center" data-id="element-48">
          <div className="w-16 h-16 rounded-full bg-[#1A237E]/50 border-4 border-[#00E4BB] flex items-center justify-center mr-4" data-id="element-49">
            <span className="text-xl font-bold text-[#00E4BB]" data-id="element-50">
              {hydrationScore}
            </span>
          </div>
          <div data-id="element-51">
            <h3 className="text-white font-bold" data-id="element-52">Hydration Score</h3>
            <p className="text-sm text-gray-300" data-id="element-53">Better than 65% of users</p>
            <div className="flex items-center mt-1 text-[#A6FF4D] text-xs" data-id="element-54">
              <TrendingUpIcon className="w-3 h-3 mr-1" data-id="element-55" />
              <span data-id="element-56">+12% from last week</span>
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-id="element-57">
          <div className="bg-[#1A237E]/50 rounded-lg p-2" data-id="element-58">
            <p className="text-xs text-gray-300" data-id="element-59">Sleep Quality</p>
            <p className="text-[#00E4BB] font-medium" data-id="element-60">Good</p>
          </div>
          <div className="bg-[#1A237E]/50 rounded-lg p-2" data-id="element-61">
            <p className="text-xs text-gray-300" data-id="element-62">Energy Level</p>
            <p className="text-[#00E4BB] font-medium" data-id="element-63">High</p>
          </div>
          <div className="bg-[#1A237E]/50 rounded-lg p-2" data-id="element-64">
            <p className="text-xs text-gray-300" data-id="element-65">Skin Health</p>
            <p className="text-[#00E4BB] font-medium" data-id="element-66">Improving</p>
          </div>
        </div>
      </GlassCard>

      {/* Functional Shots Recommendation */}
      <GlassCard className="mb-6 p-4" data-id="element-67">
        <div className="flex justify-between items-center mb-3" data-id="element-68">
          <h3 className="text-white font-bold" data-id="element-69">Functional Shots</h3>
          <Button size="sm" variant="outline" className="text-xs border-[#00E4BB]/30 text-[#00E4BB]" onClick={toggleFunctionalShots} data-id="element-70">
            {functionalShotsVisible ? 'Hide' : 'View All'}
          </Button>
        </div>
        <div className="bg-[#1A237E]/50 rounded-lg p-3 mb-3" data-id="element-71">
          <div className="flex items-start" data-id="element-72">
            <div className="w-10 h-10 rounded-full bg-[#FF9800]/20 flex items-center justify-center mr-3 flex-shrink-0" data-id="element-73">
              <ZapIcon className="w-5 h-5 text-[#FF9800]" data-id="element-74" />
            </div>
            <div data-id="element-75">
              <h4 className="text-white font-medium" data-id="element-76">
                Recommended: Energy Boost
              </h4>
              <p className="text-sm text-gray-300 mb-2" data-id="element-77">
                Perfect for your workout later today. Natural caffeine & B
                vitamins to enhance performance.
              </p>
              <Button size="sm" className="bg-[#FF9800] text-white border-none text-xs" data-id="element-78">
                Add to Next Drink
              </Button>
            </div>
          </div>
        </div>
        {functionalShotsVisible && <div className="grid grid-cols-2 gap-3" data-id="element-79">
            {functionalShots.map(shot => <div key={shot.id} className="bg-[#1A237E]/50 rounded-lg p-3" data-id="element-80">
                <div className="flex items-center mb-2" data-id="element-81">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center mr-2" style={{
              backgroundColor: `${shot.color}20`
            }} data-id="element-82">
                    <shot.icon className="w-4 h-4" style={{
                color: shot.color
              }} data-id="element-83" />
                  </div>
                  <h4 className="text-white text-sm font-medium" data-id="element-84">
                    {shot.name}
                  </h4>
                </div>
                <p className="text-xs text-gray-300 mb-2" data-id="element-85">{shot.description}</p>
                <Button size="sm" fullWidth className="text-xs py-1" style={{
            backgroundColor: shot.color
          }} data-id="element-86">
                  Add
                </Button>
              </div>)}
          </div>}
      </GlassCard>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto mb-4" data-id="element-87">
        <div className="space-y-4" data-id="element-88">
          {messages.map(msg => <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-89">
              <div className={`max-w-[80%] rounded-2xl p-3 ${msg.sender === 'user' ? 'bg-[#00E4BB] text-[#002B36]' : 'bg-[#002B36]/60 border border-[#00E4BB]/30 text-white'}`} data-id="element-90">
                <div className="flex items-center mb-1" data-id="element-91">
                  {msg.sender === 'bot' ? <BotIcon className="w-4 h-4 mr-1" data-id="element-92" /> : <UserIcon className="w-4 h-4 mr-1" data-id="element-93" />}
                  <span className="text-xs opacity-70" data-id="element-94">{msg.time}</span>
                </div>
                <p className="text-sm" data-id="element-95">{msg.text}</p>
              </div>
            </div>)}
        </div>
      </div>

      {/* Message Input */}
      <div className="relative" data-id="element-96">
        <input type="text" value={message} onChange={e => setMessage(e.target.value)} placeholder="Ask your hydration coach..." className="w-full bg-[#002B36]/60 border border-[#00E4BB]/30 rounded-full px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[#00E4BB]" data-id="element-97" />
        <button className="absolute right-2 top-2 w-8 h-8 bg-[#00E4BB] rounded-full flex items-center justify-center" onClick={handleSendMessage} data-id="element-98">
          <SendIcon className="w-4 h-4 text-[#002B36]" data-id="element-99" />
        </button>
      </div>

      {/* Quick Suggestions */}
      <div className="mt-6" data-id="element-100">
        <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-101">
          <button className="flex items-center gap-2 bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-full px-4 py-2 text-[#00E4BB] text-sm whitespace-nowrap hover:bg-[#00E4BB]/10 transition-colors" data-id="element-102">
            <DropletIcon className="w-4 h-4" data-id="element-103" />
            <span data-id="element-104">More today?</span>
          </button>
          <button className="flex items-center gap-2 bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-full px-4 py-2 text-[#00E4BB] text-sm whitespace-nowrap hover:bg-[#00E4BB]/10 transition-colors" data-id="element-105">
            <MoonIcon className="w-4 h-4" data-id="element-106" />
            <span data-id="element-107">Sleep tips</span>
          </button>
          <button className="flex items-center gap-2 bg-[#1A237E]/60 border border-[#00E4BB]/30 rounded-full px-4 py-2 text-[#00E4BB] text-sm whitespace-nowrap hover:bg-[#00E4BB]/10 transition-colors" data-id="element-108">
            <TrendingUpIcon className="w-4 h-4" data-id="element-109" />
            <span data-id="element-110">My stats</span>
          </button>
        </div>
      </div>
    </div>;
};
export default AICoaching;